import React, { useState, Fragment } from 'react';
import config from '../../utils/config';
import { useAuth } from '../../portal/context/auth-context';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Dialog, Transition } from '@headlessui/react';

export default function AddNewShippingForm({ setNewShipping, handleNewAddress }) {
    const ORIGIN = config.origin;
    const { currentUser } = useAuth();

    const [open, setOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [newAddressData, setNewAddressData] = useState({
        shipAddressName: '',
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        accountId: currentUser.Account.Id,
    });

    const postNewAddress = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const response = await fetch(`${ORIGIN}/cp/shipping/new-address`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: currentUser?.tokenId,
            },
            body: JSON.stringify(newAddressData),
        });
        const data = await response.json();
        setIsLoading(false);

        const newAddress = {
            shipAddressId: data.shipAddressId,
            selected: true,
            ...newAddressData,
        };

        handleNewAddress(newAddress);
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as='div' className='relative z-20' onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter='ease-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in duration-200'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                >
                    <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
                </Transition.Child>

                <div className='overflow-y-auto fixed inset-0 z-10'>
                    <div className='flex justify-center items-end p-4 min-h-full text-center sm:items-center sm:p-0'>
                        <Transition.Child
                            as={Fragment}
                            enter='ease-out duration-300'
                            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                            enterTo='opacity-100 translate-y-0 sm:scale-100'
                            leave='ease-in duration-200'
                            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                        >
                            <Dialog.Panel className='overflow-hidden relative px-4 pt-5 pb-4 max-w-lg text-left bg-white rounded-lg shadow-xl transition-all transform sm:my-8 sm:w-full sm:p-6'>
                                <div>
                                    <div className='mt-3 text-center sm:mt-5'>
                                        <div className='relative'>
                                            <button
                                                className='flex absolute right-1 -top-2 justify-center items-center mt-1 mr-2 w-6 h-6 text-white rounded-full cursor-pointer bg-blue-primary hover:bg-gray-400'
                                                onClick={() => {
                                                    setOpen(false);
                                                    setNewShipping(false);
                                                }}
                                            >
                                                <XMarkIcon className='w-5 h-5' />
                                            </button>
                                        </div>
                                        <div className='px-2 py-5 mt-5 md:mt-0'>
                                            <h1 className='text-2xl font-bold text-gray-700'>Add New Shipping Address</h1>
                                            <form>
                                                <div className='overflow-hidden p-2'>
                                                    <div className='px-4 py-5 bg-white sm:p-6'>
                                                        <div className='grid grid-cols-6 gap-6'>
                                                            <div className='col-span-6'>
                                                                <label
                                                                    htmlFor='first-name'
                                                                    className='block text-sm font-medium text-gray-700'
                                                                >
                                                                    Address Name
                                                                </label>
                                                                <input
                                                                    onChange={(e) => {
                                                                        setNewAddressData({
                                                                            ...newAddressData,
                                                                            shipAddressName: e.target.value,
                                                                        });
                                                                    }}
                                                                    type='text'
                                                                    name='shipAddressName'
                                                                    id='shipAddressName'
                                                                    className='block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                                                                />
                                                            </div>

                                                            <div className='col-span-6'>
                                                                <label
                                                                    htmlFor='street-address'
                                                                    className='block text-sm font-medium text-gray-700'
                                                                >
                                                                    Street address
                                                                </label>
                                                                <textarea
                                                                    onChange={(e) => {
                                                                        setNewAddressData({
                                                                            ...newAddressData,
                                                                            street: e.target.value,
                                                                        });
                                                                    }}
                                                                    type='text'
                                                                    name='street'
                                                                    id='street'
                                                                    rows={4}
                                                                    autoComplete='street-address'
                                                                    className='block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                                                                ></textarea>
                                                            </div>

                                                            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
                                                                <label
                                                                    htmlFor='city'
                                                                    className='block text-sm font-medium text-gray-700'
                                                                >
                                                                    City
                                                                </label>
                                                                <input
                                                                    onChange={(e) => {
                                                                        setNewAddressData({
                                                                            ...newAddressData,
                                                                            city: e.target.value,
                                                                        });
                                                                    }}
                                                                    type='text'
                                                                    name='city'
                                                                    id='city'
                                                                    autoComplete='address-level2'
                                                                    className='block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                                                                />
                                                            </div>

                                                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                                                <label
                                                                    htmlFor='region'
                                                                    className='block text-sm font-medium text-gray-700'
                                                                >
                                                                    State / Province
                                                                </label>
                                                                <input
                                                                    onChange={(e) => {
                                                                        setNewAddressData({
                                                                            ...newAddressData,
                                                                            state: e.target.value,
                                                                        });
                                                                    }}
                                                                    type='text'
                                                                    name='region'
                                                                    id='region'
                                                                    autoComplete='address-level1'
                                                                    className='block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                                                                />
                                                            </div>

                                                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                                                <label
                                                                    htmlFor='postal-code'
                                                                    className='block text-sm font-medium text-gray-700'
                                                                >
                                                                    ZIP / Postal code
                                                                </label>
                                                                <input
                                                                    onChange={(e) => {
                                                                        setNewAddressData({
                                                                            ...newAddressData,
                                                                            postalCode: e.target.value,
                                                                        });
                                                                    }}
                                                                    type='text'
                                                                    name='postal-code'
                                                                    id='postal-code'
                                                                    autoComplete='postal-code'
                                                                    className='block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                                                                />
                                                            </div>
                                                            <div className='col-span-6 sm:col-span-3'>
                                                                <label
                                                                    htmlFor='country'
                                                                    className='block text-sm font-medium text-gray-700'
                                                                >
                                                                    Country
                                                                </label>
                                                                <input
                                                                    onChange={(e) => {
                                                                        setNewAddressData({
                                                                            ...newAddressData,
                                                                            country: e.target.value,
                                                                        });
                                                                    }}
                                                                    type='text'
                                                                    name='country'
                                                                    id='country'
                                                                    autoComplete='country'
                                                                    className='block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='px-4 py-3 text-right sm:px-6'>
                                                        <button
                                                            disabled={
                                                                newAddressData.shipAddressName === '' ||
                                                                newAddressData.street === '' ||
                                                                newAddressData.city === '' ||
                                                                newAddressData.state === '' ||
                                                                newAddressData.postalCode === '' ||
                                                                newAddressData.country === '' ||
                                                                isLoading
                                                            }
                                                            onClick={postNewAddress}
                                                            type='button'
                                                            className='inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white rounded-md border border-transparent shadow-sm cursor-pointer bg-blue-primary hover:bg-blue-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-primary'
                                                        >
                                                            {isLoading ? (
                                                                <>
                                                                    <svg
                                                                        role='status'
                                                                        className='inline mr-3 w-4 h-4 text-white animate-spin'
                                                                        viewBox='0 0 100 101'
                                                                        fill='none'
                                                                        xmlns='http://www.w3.org/2000/svg'
                                                                    >
                                                                        <path
                                                                            d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                                                                            fill='#E5E7EB'
                                                                        />
                                                                        <path
                                                                            d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                                                                            fill='currentColor'
                                                                        />
                                                                    </svg>
                                                                    loading...
                                                                </>
                                                            ) : (
                                                                'Add Address'
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
